import React, { useState, useEffect } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCTextInput from '@/Atoms/CCTextInput';
import CCDatePicker from '@/Atoms/CCDatePicker';
import SlideModal from '@/Atoms/SlideModal';
import Spacer from '@/Atoms/Spacer';
import Box from '@/Atoms/Box';
import CCColors from '@/Utils/CCColors';
import CCAutoComplete from '@/Atoms/CCAutoComplete';
import { vs, FontSizes } from '@/Utils/Dimensions';
import { formatDateToYYYYMMDD } from '@/Utils/CommonUtils';
import { useMediaQuery } from '@/Hooks/useMediaQuery/useMediaQuery';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import useDebouncedValue from '@/Hooks/useDebouncedValue';
import useTournamentNameAutocomplete from '@/Hooks/GrandMastersGamesApi/useTournamentNameAutocomplete';
import useCreateAndPublishOTBGamesApi from '@/Hooks/GetOTBGameDetails/useCreateAndPublishOTBGamesApi';
import AlertModal from '@/Components/AlertModal';
import { useNavigation } from '@react-navigation/native';
import { ScreenName } from '@/navigators/StackConstants';
import Dropzone from 'react-dropzone';
import upload from '@/Assets/Images/png/upload.png';
import fileIcon from '@/Assets/Images/png/file.png';
import deleteIcon from '@/Assets/Images/png/delete.png';

interface AddTournamentModalProps {
  visible: boolean;
  setVisible: (v: boolean) => void;
  submitCallback?: () => void;
}

interface TournamentDetails {
  tournamentName: string;
  tournamentId: string;
  location: string;
  startDate: string;
  endDate: string;
  playerName: string;
  playerFideId: string;
}

interface ParsedGame {
  date: string;
  round: string;
  white: string;
  black: string;
  whiteElo: string;
  blackElo: string;
  result: string;
  pgn: string;
}

// PGN placeholder values that mean "no name"
const PGN_EMPTY = new Set(['*', '?', '-', '']);
const normPgnField = (v: string) => (PGN_EMPTY.has(v.trim()) ? '' : v.trim());

const MAX_GAMES = 20;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_PASTE_CHARS = 5 * 1024 * 1024; // 5 MB

// limit+1 so we can detect "too many" without parsing the whole file
function parsePGN(pgn: string, limit = MAX_GAMES + 1): ParsedGame[] {
  const games: ParsedGame[] = [];
  // Strip BOM and normalize line endings before splitting
  const cleaned = pgn.replace(/﻿/g, '').replace(/\r\n/g, '\n');
  const blocks = cleaned.split(/\n\s*\n(?=\[)/).filter(Boolean);

  for (const block of blocks) {
    if (games.length >= limit) break; // stop early — no need to parse beyond limit
    const tag = (name: string) => {
      const m = block.match(new RegExp(`\\[${name}\\s+"([^"]*)"\\]`));
      return m ? m[1] : '';
    };
    const date = tag('Date').replace(/\./g, '-');
    const round = tag('Round');
    const white = normPgnField(tag('White'));
    const black = normPgnField(tag('Black'));
    const whiteElo = tag('WhiteElo');
    const blackElo = tag('BlackElo');
    const result = tag('Result');
    // Only include blocks that have moves (contain a move number like "1.")
    if ((white || black || result) && /\b1\./.test(block)) {
      games.push({ date, round, white, black, whiteElo, blackElo, result, pgn: block.trim() });
    }
  }
  return games;
}

function resultLabel(raw: string) {
  if (raw === '1-0') return 'White wins (1–0)';
  if (raw === '0-1') return 'Black wins (0–1)';
  if (raw === '1/2-1/2') return 'Draw (½–½)';
  return raw || '—';
}

// Split input by spaces/commas and check if any significant token appears in the PGN name.
// Handles "Last, First" PGN format vs "First Last" or partial user input.
function playerMatchesPgnName(playerInput: string, pgnName: string): boolean {
  const tokens = playerInput
    .trim()
    .toLowerCase()
    .split(/[\s,]+/)
    .filter(t => t.length > 2);
  const pgnL = pgnName.toLowerCase();
  return tokens.length > 0 && tokens.some(t => pgnL.includes(t));
}

// Normalise "Last, First" → "First Last" for display in game review.
function normalizePgnName(name: string): string {
  const parts = name.split(',').map(s => s.trim());
  if (parts.length === 2 && parts[1]) return `${parts[1]} ${parts[0]}`;
  return name;
}

// Find the player name that appears in every game that has names (in either color).
// Returns a prefill name when unambiguous, otherwise a hint message for the user.
function detectPlayerName(games: ParsedGame[]): { name: string; hint: string } {
  if (games.length === 0) return { name: '', hint: '' };

  // Only consider games that actually have at least one real player name
  const namedGames = games.filter(g => g.white || g.black);

  if (namedGames.length === 0)
    return { name: '', hint: 'No player names found in PGN — please enter your name manually' };
  if (namedGames.length === 1)
    return { name: '', hint: 'Only 1 game with player names found — enter your name as it appears in the PGN' };

  const counts = new Map<string, number>();
  for (const g of namedGames) {
    for (const name of new Set([g.white, g.black].filter(Boolean))) {
      counts.set(name, (counts.get(name) || 0) + 1);
    }
  }
  const common = [...counts.entries()]
    .filter(([, count]) => count === namedGames.length)
    .map(([name]) => name);

  if (common.length === 1) return { name: normalizePgnName(common[0]), hint: '' };
  if (common.length === 0)
    return { name: '', hint: 'No single player appears in all games — please enter your name manually' };
  return { name: '', hint: 'Multiple players appear in all games — please enter your name manually' };
}

// Extract tournament-level metadata from the first PGN block.
function extractPgnMeta(pgn: string) {
  const firstBlock = pgn.split(/\n\s*\n(?=\[)/)[0] || pgn;
  const tag = (name: string) => {
    const m = firstBlock.match(new RegExp(`\\[${name}\\s+"([^"]*)"\\]`));
    return m ? m[1] : '';
  };
  const toDate = (d: string) =>
    /^\d{4}\.\d{2}\.\d{2}$/.test(d) ? d.replace(/\./g, '-') : '';
  // EventDate can be "2026.??.??" on ChessBase — validate before using it
  const startDate = toDate(tag('EventDate')) || toDate(tag('Date'));
  const site = tag('Site');
  // Discard placeholder "?" and URLs (Lichess exports site as a URL)
  const location =
    site && site !== '?' && !site.startsWith('http') ? site : '';
  return {
    eventName: tag('Event'),
    site: location,
    startDate,
  };
}

const RESULT_CHIPS = [
  { label: 'W', value: 1 },
  { label: 'D', value: 0.5 },
  { label: 'L', value: 0 },
] as const;

const STEPS = ['Upload PGN', 'Tournament Details', 'Confirm & Upload'];

function StepIndicator({ currentStep }: { currentStep: number }) {
  return (
    <View style={stepStyles.wrapper}>
      {STEPS.map((label, idx) => {
        const isCompleted = idx < currentStep;
        const isActive = idx === currentStep;
        return (
          <View key={idx} style={stepStyles.stepRow}>
            <View
              style={[
                stepStyles.circle,
                isCompleted && stepStyles.circleCompleted,
                isActive && stepStyles.circleActive,
              ]}>
              {isCompleted ? (
                <CCText style={stepStyles.circleTextDone}>✓</CCText>
              ) : (
                <CCText
                  style={[
                    stepStyles.circleText,
                    isActive && stepStyles.circleTextActive,
                  ]}>
                  {idx + 1}
                </CCText>
              )}
            </View>
            <CCText
              style={[
                stepStyles.label,
                isActive && stepStyles.labelActive,
                isCompleted && stepStyles.labelCompleted,
              ]}>
              {label}
            </CCText>
            {idx < STEPS.length - 1 && (
              <View
                style={[
                  stepStyles.line,
                  isCompleted && stepStyles.lineCompleted,
                ]}
              />
            )}
          </View>
        );
      })}
    </View>
  );
}

const dropContainerStyle: React.CSSProperties = {
  borderRadius: 8,
  border: '1px dashed rgba(34,34,34,0.20)',
  backgroundColor: CCColors.Primary.lightBrown,
  height: 150,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  position: 'relative',
};

function Step1PGN({
  pgnText,
  setPgnText,
  file,
  setFile,
}: {
  pgnText: string;
  setPgnText: (t: string) => void;
  file: File | undefined;
  setFile: (f: File | undefined) => void;
}) {
  const [activeTab, setActiveTab] = useState<'file' | 'text'>('file');
  const [popup, setPopup] = useState<{ header: string; bodyTxt: string } | null>(null);

  const handleDrop = async (acceptedFiles: File[]) => {
    const dropped = acceptedFiles[0];
    if (!dropped) return;
    if (dropped.size > MAX_FILE_BYTES) {
      setPgnText('');
      setFile(undefined);
      setPopup({
        header: 'File Too Large',
        bodyTxt: `File is too large (${(dropped.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is ${MAX_FILE_BYTES / 1024 / 1024} MB.`,
      });
      return;
    }
    setFile(dropped);
    const text = await dropped.text();
    setPgnText(text);
  };

  return (
    <View>
      <AlertModal
        open={!!popup}
        header={popup?.header}
        bodyTxt={popup?.bodyTxt}
        handleClose={() => setPopup(null)}
      />

      <View style={s1.tabRow}>
        {(['file', 'text'] as const).map(tab => (
          <TouchableOpacity
            key={tab}
            style={[s1.tab, activeTab === tab && s1.tabActive]}
            onPress={() => setActiveTab(tab)}>
            <CCText style={[s1.tabText, activeTab === tab && s1.tabTextActive]}>
              {tab === 'file' ? 'Upload File' : 'Paste PGN Text'}
            </CCText>
          </TouchableOpacity>
        ))}
      </View>

      <Spacer spacing={vs(12)} />

      {activeTab === 'file' ? (
        !file ? (
          <Dropzone
            accept={{ 'application/x-chess-pgn': ['.pgn'] }}
            maxFiles={1}
            onDrop={handleDrop}>
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()} style={dropContainerStyle}>
                <input {...getInputProps()} />
                <img src={upload} alt="Upload Icon" />
                <Spacer spacing={vs(12)} />
                <CCText textAlign="center">
                  Drag and drop file {'\n'}or click here to select file
                </CCText>
              </div>
            )}
          </Dropzone>
        ) : (
          <>
            <div style={dropContainerStyle}>
              <img src={fileIcon} style={{ width: 100 }} alt="File Icon" />
              <Spacer spacing={vs(12)} />
              <CCText textAlign="center" style={{ width: '80%' }}>
                {file.name}
              </CCText>
              <img
                src={deleteIcon}
                style={s1.deleteIcon}
                alt="Delete"
                onClick={() => {
                  setFile(undefined);
                  setPgnText('');
                }}
              />
            </div>
            <Spacer spacing={vs(5)} />
            <CCText color={CCColors.Green}>
              PGN file uploaded successfully
            </CCText>
          </>
        )
      ) : (
        <>
          <CCTextInput
            style={s1.textArea}
            placeholder="Paste your PGN content here…"
            placeholderTextColor={CCColors.TextColor.Grey}
            multiline
            value={pgnText}
            onChangeText={v => {
              if (v.length > MAX_PASTE_CHARS) {
                setPopup({
                  header: 'Text Too Large',
                  bodyTxt: `Pasted text is too large (${(v.length / 1024 / 1024).toFixed(1)} MB). Maximum allowed is ${MAX_PASTE_CHARS / 1024 / 1024} MB.`,
                });
                return;
              }
              setPgnText(v);
              setFile(undefined);
            }}
            fontSize={12}
          />
          <CCText style={s1.charCount}>{pgnText.length} chars</CCText>
        </>
      )}
    </View>
  );
}

function Step2Details({
  details,
  onChangeField,
  onTournamentSelect,
  tournamentData,
  isLoading,
  isMobileOrTab,
  dropDownClose,
  setDropDownClose,
  pgnNameHint,
}: {
  details: TournamentDetails;
  onChangeField: (field: keyof TournamentDetails, value: string) => void;
  onTournamentSelect: (id: string, name: string) => void;
  tournamentData: { id: string; name: string }[];
  isLoading: boolean;
  isMobileOrTab: boolean;
  dropDownClose: boolean;
  setDropDownClose: (v: boolean) => void;
  pgnNameHint: string;
}) {
  const [itemSelected, setItemSelected] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  return (
    <View>
      <CCText fontWeight="bold" style={styles.inputLabelStyle}>
        Tournament:
      </CCText>
      <View style={{ zIndex: 100, position: 'relative' }}>
        <CCAutoComplete
          id={20}
          listHeight={200}
          placeholder="Search tournament name"
          value={details.tournamentName}
          onChangeText={text => {
            onChangeField('tournamentName', text);
            onChangeField('tournamentId', '');
            setItemSelected(false);
            setDropDownClose(false);
          }}
          handleFocus={() => {
            setItemSelected(false);
            onChangeField('tournamentName', '');
            setIsFocused(true);
            setDropDownClose(false);
          }}
          handleBlur={() => {
            setTimeout(() => {
              if (itemSelected) {
                setIsFocused(false);
                setDropDownClose(true);
              }
            }, 150);
          }}
          onKeyPress={() => setItemSelected(true)}
          dropDownClose={dropDownClose}
          data={tournamentData}
          onPressOnItem={item => {
            setItemSelected(true);
            onTournamentSelect(String(item.item.id), item.item.name);
            setDropDownClose(true);
          }}
          loading={isLoading}
        />
      </View>

      <CCText fontWeight="bold" style={styles.inputLabelStyle}>
        Location / City: (optional)
      </CCText>
      <CCTextInput
        placeholder="e.g. Mumbai"
        onChangeText={v => onChangeField('location', v)}
        value={details.location}
        style={{ width: '100%' }}
        fontSize={14}
        placeholderTextColor={CCColors.TextColor.Grey}
      />

      <CCText fontWeight="bold" style={styles.inputLabelStyle}>
        Start Date:
      </CCText>
      <CCDatePicker
        onChange={text =>
          onChangeField('startDate', formatDateToYYYYMMDD(text))
        }
        value={details.startDate}
        isMobileOrTab={isMobileOrTab}
      />

      <CCText fontWeight="bold" style={styles.inputLabelStyle}>
        End Date: (optional)
      </CCText>
      <CCDatePicker
        onChange={text => onChangeField('endDate', formatDateToYYYYMMDD(text))}
        value={details.endDate}
        isMobileOrTab={isMobileOrTab}
      />

      <CCText fontWeight="bold" style={styles.inputLabelStyle}>
        Your Name (as in PGN):
      </CCText>
      <CCTextInput
        placeholder="e.g. Sharma, Arjun"
        onChangeText={v => onChangeField('playerName', v)}
        value={details.playerName}
        style={{ width: '100%' }}
        fontSize={14}
        placeholderTextColor={CCColors.TextColor.Grey}
      />
      {!!pgnNameHint && (
        <>
          <Spacer spacing={vs(4)} />
          <CCText fontSize={12} color={CCColors.TextColor.Grey}>{pgnNameHint}</CCText>
        </>
      )}

      <CCText fontWeight="bold" style={styles.inputLabelStyle}>
        Your FIDE ID: (optional)
      </CCText>
      <CCTextInput
        placeholder="e.g. 5000017"
        onChangeText={v => onChangeField('playerFideId', v)}
        value={details.playerFideId}
        style={{ width: '100%' }}
        fontSize={14}
        placeholderTextColor={CCColors.TextColor.Grey}
        keyboardType="numeric"
      />
    </View>
  );
}

function Step3Confirm({
  details,
  parsedGames,
  fileName,
  gameOverrides,
  onGameResultChange,
}: {
  details: TournamentDetails;
  parsedGames: ParsedGame[];
  fileName: string;
  gameOverrides: Record<number, number>;
  onGameResultChange: (index: number, result: number) => void;
}) {
  return (
    <View>
      <View style={s3.card}>
        <CCText style={s3.cardTitle}>Tournament Info</CCText>
        <View style={s3.divider} />
        {[
          ['Tournament', details.tournamentName || '—'],
          ['Tournament ID', details.tournamentId || 'None (new)'],
          ['Location', details.location || '—'],
          ['Start Date', details.startDate || '—'],
          ['End Date', details.endDate || '—'],
          ['Player Name', details.playerName || '—'],
          ['FIDE ID', details.playerFideId || '—'],
        ].map(([label, value]) => (
          <View key={label} style={s3.row}>
            <CCText style={s3.rowLabel}>{label}</CCText>
            <CCText style={s3.rowValue}>{value}</CCText>
          </View>
        ))}
      </View>

      <Spacer spacing={vs(16)} />

      <View style={s3.card}>
        <CCText style={s3.cardTitle}>PGN Summary</CCText>
        <View style={s3.divider} />
        <View style={s3.row}>
          <CCText style={s3.rowLabel}>Source</CCText>
          <CCText style={s3.rowValue}>{fileName || 'Pasted text'}</CCText>
        </View>
        <View style={s3.row}>
          <CCText style={s3.rowLabel}>Games found</CCText>
          <CCText style={[s3.rowValue, s3.badge]}>{parsedGames.length}</CCText>
        </View>
      </View>

      {parsedGames.length > 0 && (
        <>
          <Spacer spacing={vs(16)} />
          <View style={s3.card}>
            <CCText style={s3.cardTitle}>Games Preview — tap W/D/L to correct result</CCText>
            <View style={s3.divider} />
            <View style={[s3.row, s3.tableHeader]}>
              <CCText style={[s3.col, s3.colHeader]}>Rd</CCText>
              <CCText style={[s3.col2, s3.colHeader]}>Opponent</CCText>
              <CCText style={[s3.col, s3.colHeader]}>Color</CCText>
              <CCText style={[s3.col2, s3.colHeader]}>Your Result</CCText>
            </View>

            {parsedGames.map((g, i) => {
              const isWhite = playerMatchesPgnName(details.playerName, g.white);
              const color = isWhite ? 'White' : 'Black';
              const opponentName = normalizePgnName(isWhite ? g.black : g.white);

              let computedResult: number;
              if (g.result === '1-0') computedResult = isWhite ? 1 : 0;
              else if (g.result === '0-1') computedResult = isWhite ? 0 : 1;
              else computedResult = 0.5;

              const effective =
                gameOverrides[i] !== undefined ? gameOverrides[i] : computedResult;

              return (
                <View key={i} style={[s3.row, i % 2 === 1 && s3.rowAlt]}>
                  <CCText style={s3.col}>{g.round || '—'}</CCText>
                  <CCText style={s3.col2} numberOfLines={1}>
                    {opponentName || '—'}
                  </CCText>
                  <CCText style={[s3.col, { color: isWhite ? CCColors.TextColor.Primary : CCColors.TextColor.Grey }]}>
                    {color}
                  </CCText>
                  <View style={s3.chipRow}>
                    {RESULT_CHIPS.map(chip => (
                      <TouchableOpacity
                        key={chip.label}
                        style={[s3.chip, effective === chip.value && s3.chipActive]}
                        onPress={() => onGameResultChange(i, chip.value)}>
                        <CCText style={[s3.chipText, effective === chip.value && s3.chipTextActive]}>
                          {chip.label}
                        </CCText>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              );
            })}
          </View>
        </>
      )}
    </View>
  );
}

export default function AddTournamentModal({
  visible,
  setVisible,
  submitCallback,
}: AddTournamentModalProps) {
  const { isMobileOrTab } = useMediaQuery();
  const navigation = useNavigation();
  const [step, setStep] = useState(0);
  const [pgnText, setPgnText] = useState('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [stepError, setStepError] = useState<string>('');
  const [gameOverrides, setGameOverrides] = useState<Record<number, number>>({});
  const [pgnNameHint, setPgnNameHint] = useState('');
  const [details, setDetails] = useState<TournamentDetails>({
    tournamentName: '',
    tournamentId: '',
    location: '',
    startDate: '',
    endDate: '',
    playerName: '',
    playerFideId: '',
  });

  const { mutate: createAndPublishGames } = useCreateAndPublishOTBGamesApi(
    _data => {
      setUploading(false);
      submitCallback?.();
      handleClose();
      navigation.navigate(ScreenName.Games as never);
    },
    error => {
      setUploading(false);
      setUploadError(error?.message || 'Upload failed. Please try again.');
    },
  );

  const [tournamentData, setTournamentData] = useState<
    { id: string; name: string }[]
  >([]);
  const [dropDownClose, setDropDownClose] = useLoggedInUserStore(state => [
    state.dropDownClose,
    state.setDropDownClose,
  ]);

  const debouncedTournamentName = useDebouncedValue(details.tournamentName, 500);

  const {
    data: tournamentAutoComplete,
    isFetching: tournamentFetching,
  } = useTournamentNameAutocomplete(debouncedTournamentName, {
    enabled: !!debouncedTournamentName,
    minLength: 3,
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
  });

  const shouldShowAutocomplete = debouncedTournamentName.trim().length >= 3;

  useEffect(() => {
    if (!shouldShowAutocomplete) {
      setTournamentData([]);
      setDropDownClose(true);
      return;
    }

    const items = tournamentAutoComplete?.data ?? [];
    if (items.length) {
      setTournamentData(items.map(item => ({ id: String(item.id), name: item.name })));
      setDropDownClose(false);
    } else {
      setTournamentData([]);
      setDropDownClose(true);
    }
  }, [
    shouldShowAutocomplete,
    tournamentAutoComplete,
    setDropDownClose,
    setTournamentData,
  ]);

  const handleDetailChange = (field: keyof TournamentDetails, value: string) =>
    setDetails(prev => ({ ...prev, [field]: value }));

  const handleTournamentSelect = (id: string, name: string) => {
    setDetails(prev => ({ ...prev, tournamentId: id, tournamentName: name }));
    setTournamentData([]);
  };

  // liveParsed is only used on step 0 for preview/validation.
  // parsedGames is locked in state when the user advances past step 0,
  // at which point pgnText is cleared to free the large raw string.
  const liveParsed = React.useMemo(
    () => (pgnText ? parsePGN(pgnText) : []),
    [pgnText],
  );
  const [parsedGames, setParsedGames] = useState<ParsedGame[]>([]);

  // Prefill tournament details and player name from PGN when loaded (step 0 only)
  useEffect(() => {
    if (!pgnText) return;
    const meta = extractPgnMeta(pgnText);
    const detected = detectPlayerName(liveParsed);
    setPgnNameHint(detected.hint);
    setDetails(prev => ({
      ...prev,
      tournamentName: prev.tournamentName || meta.eventName,
      location: prev.location || meta.site,
      startDate: prev.startDate || meta.startDate,
      playerName: prev.playerName || detected.name,
    }));
    setGameOverrides({});
  }, [pgnText, liveParsed]);

  const tooManyGames = liveParsed.length > MAX_GAMES;
  const hasContent = pgnText.trim().length > 0 || !!file;
  const isInvalidPgn = pgnText.trim().length > 0 && liveParsed.length === 0;
  const canAdvanceStep1 = hasContent && !tooManyGames && !isInvalidPgn;

  // Lock in parsed games and free the raw PGN string from memory
  const handleAdvanceFromStep1 = () => {
    setParsedGames(liveParsed);
    setPgnText('');
    setStep(s => s + 1);
  };
  const canAdvanceStep2 =
    !!details.tournamentName.trim() &&
    !!details.startDate.trim() &&
    !!details.playerName.trim();

  const handleClose = () => {
    setStep(0);
    setPgnText('');
    setFile(undefined);
    setUploadError('');
    setStepError('');
    setGameOverrides({});
    setPgnNameHint('');
    setParsedGames([]);
    setDetails({
      tournamentName: '',
      tournamentId: '',
      location: '',
      startDate: '',
      endDate: '',
      playerName: '',
      playerFideId: '',
    });
    setVisible(false);
  };

  const handleAdvanceFromStep2 = () => {
    if (!canAdvanceStep2) return;
    // If the PGN contains no player names at all, skip the match check —
    // the user has manually filled in their name.
    const pgnHasNames = parsedGames.some(g => g.white || g.black);
    if (pgnHasNames) {
      const hasMatch = parsedGames.some(
        g =>
          playerMatchesPgnName(details.playerName, g.white) ||
          playerMatchesPgnName(details.playerName, g.black),
      );
      if (!hasMatch) {
        setStepError(
          `"${details.playerName}" does not match any player name in the PGN. ` +
          'Please enter your name exactly as it appears in the PGN (e.g. "Smith, John").',
        );
        return;
      }
    }
    setStepError('');
    setStep(s => s + 1);
  };

  const handleUpload = () => {
    if (!parsedGames.length) {
      setUploadError('No games found in PGN. Please check your file.');
      return;
    }

    setUploading(true);
    setUploadError('');

    const tournamentId = details.tournamentId ? Number(details.tournamentId) : null;

    const games = parsedGames.map((game, i) => {
      // If PGN has no player names, default to White so color/opponent don't break.
      const pgnGameHasNames = !!(game.white || game.black);
      const isWhite = pgnGameHasNames
        ? playerMatchesPgnName(details.playerName, game.white)
        : true;
      const opponentPgnName = isWhite ? game.black : game.white;
      const opponentElo = isWhite ? game.blackElo : game.whiteElo;
      const color = isWhite ? 'White' : 'Black';

      let computedResult: number;
      if (game.result === '1-0') computedResult = isWhite ? 1 : 0;
      else if (game.result === '0-1') computedResult = isWhite ? 0 : 1;
      else computedResult = 0.5;

      const result =
        gameOverrides[i] !== undefined ? gameOverrides[i] : computedResult;

      return {
        game_date: details.startDate,
        round_no: isNaN(Number(game.round)) ? 1 : Number(game.round),
        opponent_rating: isNaN(Number(opponentElo)) ? 0 : Number(opponentElo),
        time_class: 'classical',
        color,
        result,
        pgn_content: game.pgn,
        tournament_id: tournamentId,
        tournament_name: details.tournamentName,
      };
    });

    createAndPublishGames({ games });
  };

  return (
    <SlideModal
      type="side"
      width={0.35}
      modalHeaderTitle={
        <View style={{ paddingVertical: 20 }}>Add Tournament</View>
      }
      visible={visible}
      onClose={handleClose}>
      <StepIndicator currentStep={step} />

      <View
        style={{
          width: '100%',
          flex: 1,
          overflow: 'auto' as any,
          padding: 20,
        }}>
        {step === 0 && (
          <Step1PGN
            pgnText={pgnText}
            setPgnText={setPgnText}
            file={file}
            setFile={setFile}
          />
        )}
        {step === 1 && (
          <Step2Details
            details={details}
            onChangeField={handleDetailChange}
            onTournamentSelect={handleTournamentSelect}
            tournamentData={tournamentData}
            isLoading={tournamentFetching}
            isMobileOrTab={isMobileOrTab}
            dropDownClose={dropDownClose}
            setDropDownClose={setDropDownClose}
            pgnNameHint={pgnNameHint}
          />
        )}
        {step === 2 && (
          <Step3Confirm
            details={details}
            parsedGames={parsedGames}
            fileName={file?.name || ''}
            gameOverrides={gameOverrides}
            onGameResultChange={(i, val) =>
              setGameOverrides(prev => ({ ...prev, [i]: val }))
            }
          />
        )}

        {isInvalidPgn && step === 0 ? (
          <>
            <Spacer spacing={vs(10)} />
            <CCText color={CCColors.Error}>
              No valid chess games found. Please make sure you are uploading or pasting a valid PGN file.
            </CCText>
          </>
        ) : null}
        {tooManyGames && step === 0 ? (
          <>
            <Spacer spacing={vs(10)} />
            <CCText color={CCColors.Error}>
              PGN contains {liveParsed.length} games. Maximum {MAX_GAMES} games per upload.
            </CCText>
          </>
        ) : null}
        {stepError ? (
          <>
            <Spacer spacing={vs(10)} />
            <CCText color={CCColors.Error}>{stepError}</CCText>
          </>
        ) : null}
        {uploadError ? (
          <>
            <Spacer spacing={vs(10)} />
            <CCText color={CCColors.Error}>{uploadError}</CCText>
          </>
        ) : null}
      </View>

      <Box style={styles.footerButton}>
        {step > 0 && (
          <>
            <CCButton
              type="outline"
              onPress={() => {
                setStep(s => s - 1);
                setStepError('');
              }}
              style={styles.backBtn}>
              <CCText fontSize={16}>← Back</CCText>
            </CCButton>
            <Spacer spacing={vs(10)} horizontal />
          </>
        )}
        {step < 2 ? (
          <CCButton
            onPress={step === 0 ? handleAdvanceFromStep1 : handleAdvanceFromStep2}
            disabled={step === 0 ? !canAdvanceStep1 : !canAdvanceStep2}>
            <CCText fontSize={16}>Continue →</CCText>
          </CCButton>
        ) : (
          <CCButton onPress={handleUpload} disabled={uploading}>
            <CCText fontSize={16}>
              {uploading
                ? `Publishing games…`
                : `Upload & Publish ${parsedGames.length} Games`}
            </CCText>
          </CCButton>
        )}
      </Box>
    </SlideModal>
  );
}

const styles = StyleSheet.create({
  inputLabelStyle: {
    marginTop: vs(16),
    marginBottom: vs(6),
  },
  footerButton: {
    padding: 20,
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: CCColors.Border,
    position: 'sticky' as any,
    bottom: 0,
    backgroundColor: CCColors.White,
  },
  backBtn: { minWidth: 90 },
});

const stepStyles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: CCColors.Primary.lightBrown,
    borderBottomWidth: 1,
    borderBottomColor: CCColors.Border,
  },
  stepRow: { flexDirection: 'row', alignItems: 'center', flex: 1 },
  circle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: CCColors.Border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: CCColors.White,
  },
  circleActive: {
    borderColor: CCColors.Primary.Default,
    backgroundColor: CCColors.Primary.Default,
  },
  circleCompleted: {
    borderColor: CCColors.Payment.Success,
    backgroundColor: CCColors.Payment.Success,
  },
  circleText: { fontSize: 12, color: CCColors.TextColor.Primary },
  circleTextActive: { color: CCColors.White, fontWeight: '700' },
  circleTextDone: { fontSize: 12, color: CCColors.White, fontWeight: '700' },
  label: {
    fontSize: 11,
    color: CCColors.TextColor.Primary,
    marginLeft: 6,
    flexShrink: 1,
  },
  labelActive: { color: CCColors.Primary.Default, fontWeight: '600' },
  labelCompleted: { color: CCColors.Payment.Success },
  line: {
    flex: 1,
    height: 2,
    backgroundColor: CCColors.Border,
    marginHorizontal: 6,
  },
  lineCompleted: { backgroundColor: CCColors.Payment.Success },
});

const s1 = StyleSheet.create({
  tabRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderColor: CCColors.Border,
  },
  tab: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: { borderBottomColor: CCColors.Primary.Default },
  tabText: { fontSize: 13, color: CCColors.TextColor.Primary },
  tabTextActive: { color: CCColors.Primary.Default, fontWeight: '600' },
  deleteIcon: {
    position: 'absolute' as any,
    right: 10,
    top: 10,
    width: 30,
    cursor: 'pointer',
  },
  textArea: { width: '100%', minHeight: 160, textAlignVertical: 'top' },
  charCount: {
    marginTop: 4,
    fontSize: 11,
    color: CCColors.TextColor.Grey,
    textAlign: 'right',
  },
});

const s3 = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: CCColors.Border,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: CCColors.White,
  },
  cardTitle: {
    fontSize: FontSizes[14],
    fontWeight: '600',
    color: CCColors.TextColor.Primary,
    paddingHorizontal: 14,
    paddingVertical: 10,
    backgroundColor: CCColors.Primary.lightBrown,
  },
  divider: {
    height: 1,
    backgroundColor: CCColors.Border,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: vs(8),
    borderBottomWidth: 1,
    borderBottomColor: CCColors.Border,
  },
  rowAlt: {
    backgroundColor: CCColors.Primary.lightBrown,
  },
  rowLabel: {
    fontSize: 13,
    color: CCColors.TextColor.Grey,
    flex: 1,
  },
  rowValue: {
    fontSize: 13,
    color: CCColors.TextColor.Primary,
    flex: 2,
    textAlign: 'right',
  },
  badge: {
    color: CCColors.Primary.Default,
    fontWeight: '700',
  },
  tableHeader: {
    backgroundColor: CCColors.Primary.lightBrown,
    borderBottomWidth: 1,
    borderBottomColor: CCColors.Border,
  },
  colHeader: {
    fontWeight: '700',
    color: CCColors.TextColor.Primary,
    fontSize: 12,
  },
  col: {
    flex: 1,
    fontSize: 12,
    color: CCColors.TextColor.Primary,
  },
  col2: {
    flex: 2,
    fontSize: 12,
    color: CCColors.TextColor.Primary,
    paddingRight: 4,
  },
  moreGames: {
    fontSize: 12,
    color: CCColors.TextColor.Grey,
    textAlign: 'center',
    paddingVertical: vs(8),
  },
  chipRow: {
    flex: 2,
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
  },
  chip: {
    paddingHorizontal: 7,
    paddingVertical: 3,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: CCColors.Border,
    backgroundColor: CCColors.White,
  },
  chipActive: {
    backgroundColor: CCColors.Primary.Default,
    borderColor: CCColors.Primary.Default,
  },
  chipText: {
    fontSize: 11,
    color: CCColors.TextColor.Primary,
  },
  chipTextActive: {
    color: CCColors.White,
    fontWeight: '700',
  },
});